/**
 * CollapsibleCard — Card whose body collapses behind a click on the
 * header. Used for the Filters & search blocks where the operator
 * wants the table front-and-centre most of the time.
 *
 * The trigger is the entire header row so the click target is
 * generous. An active-filter dot in the header lets the user spot
 * "this card has active filters" without expanding.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";

interface Props {
  readonly title: string;
  readonly defaultOpen?: boolean;
  /**
   * When true the header shows a small dot next to the title — used
   * by the parent to signal "filters are active" without forcing the
   * user to expand.
   */
  readonly hasActiveContent?: boolean;
  /** Optional right-side header content (e.g. a "Clear all" link). */
  readonly headerAction?: ReactNode;
  readonly children: ReactNode;
}

export function CollapsibleCard({
  title,
  defaultOpen = false,
  hasActiveContent = false,
  headerAction,
  children,
}: Props): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 text-left"
        >
          <svg
            className="w-3.5 h-3.5 text-simuflex-slate shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={open ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"}
            />
          </svg>
          <CardTitle>{title}</CardTitle>
          {hasActiveContent ? (
            <span
              className="inline-block w-2 h-2 rounded-full bg-simuflex-green"
              aria-label="Active filters"
              title="Active filters"
            />
          ) : null}
        </button>
        {headerAction}
      </CardHeader>
      {open ? <CardBody>{children}</CardBody> : null}
    </Card>
  );
}
